import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Video } from "lucide-react";
import { getMeetingState } from "@/lib/meetingPip";
import { goFullscreenNow } from "@/lib/pwa";
import { DesignStage, At } from "@/components/DesignStage";

/**
 * Two artwork screens:
 *   /goodbye           → "You have left the meeting"  (they can rejoin)
 *   /goodbye?ended=1   → "Thanks for joining!"        (host ended it for all)
 *
 * The rejoin button is drawn as real DOM so its text is sharp on a phone.
 * The ended screen has NO button — the class is over, there is nothing to
 * click, and tapping it used to try to rejoin a dead room.
 *
 * Button measured off the 720x1280 artwork: x 102-619  y 759-849
 */
export default function Goodbye() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const ended = params.get("ended") === "1";

  const rejoin = () => {
    // This tap is a real user gesture — spend it on fullscreen so the class
    // opens without Chrome's address bar eating the screen.
    goFullscreenNow();
    let target = "";
    try {
      // Set when they left, so "join again" always has somewhere real to go.
      target = localStorage.getItem("udyog_last_join") || "";
    } catch (_) {}
    if (!target) {
      toast.error("Please open your class link again");
      return;
    }
    nav(target, { replace: true });
  };

  // SAFETY NET: nothing but the red Leave button may end a class. If we somehow
  // land here while the session is still live (a stray back press, a browser
  // gesture, a restored tab), send the student straight back in.
  useEffect(() => {
    document.title = "Udyog Classes";
    // The class asked for full screen; the moment it is over the phone must
    // behave like a normal browser again (address bar back, tabs reachable).
    try {
      if (document.fullscreenElement) {
        const p = document.exitFullscreen?.();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } else if (document.webkitFullscreenElement) document.webkitExitFullscreen?.();
    } catch (_) {}
    if (ended) return;
    try {
      const st = getMeetingState();
      const path = sessionStorage.getItem("udyog_active_class");
      if (path && !st?.userExplicitlyLeft) {
        toast.success("You're still in class — taking you back in");
        nav(path, { replace: true });
      }
    } catch (_) {}
  }, [nav, ended]);

  if (ended) {
    return <DesignStage bg="/brand/screens/ended@2x.webp" testid="goodbye-page" />;
  }

  return (
    <DesignStage bg="/brand/screens/left@2x.webp" testid="goodbye-page">
      <At l={13.9} t={59.1} w={72.4} h={7.3}>
        <button
          onClick={rejoin}
          className="w-full h-full rounded-[3.4cqw] bg-[#20ce7d] text-black flex items-center justify-center gap-[3.5cqw] font-display active:brightness-95 transition-[filter]"
          style={{ boxShadow: "0 1.4cqw 3.6cqw -1.8cqw rgba(32,206,125,0.7)" }}
          data-testid="goodbye-rejoin"
        >
          <Video className="w-[5cqw] h-[5cqw]" strokeWidth={0} fill="currentColor" />
          <span className="block w-px h-[50%] bg-black/25" />
          <span className="font-bold" style={{ fontSize: "3.55cqw" }}>
            Join the class again
          </span>
        </button>
      </At>
    </DesignStage>
  );
}
